import { useEffect } from 'react'
import { useFeature } from '@/hooks/useSubscriptionFeatures'
import { liveChatApi } from '@/lib/api/live-chat.api'
import { useSocket } from '@/lib/socket/SocketContext'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { LIVE_CHAT_SOCKET_EVENTS } from '@scrambl/shared'

export const liveChatUnreadCountKey = ['live-chat', 'unread-count'] as const

export function useLiveChatUnreadCount(options?: { enabled?: boolean }) {
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const feature = useFeature('live_chat')
  const enabled =
    options?.enabled !== false &&
    !!organizationId &&
    !feature.isLoading &&
    feature.isEnabled
  const queryClient = useQueryClient()
  const { socket, isConnected } = useSocket()

  const query = useQuery({
    queryKey: liveChatUnreadCountKey,
    queryFn: async () => (await liveChatApi.getUnreadCount()).count,
    enabled,
    staleTime: 30_000,
    refetchInterval: isConnected ? false : 60_000,
  })

  useEffect(() => {
    if (!socket || !enabled) return

    const refresh = () => {
      void queryClient.invalidateQueries({
        queryKey: liveChatUnreadCountKey,
      })
    }

    socket.on(LIVE_CHAT_SOCKET_EVENTS.MESSAGE_CREATED, refresh)
    socket.on('NOTIFICATION_CREATED', refresh)
    socket.on('NOTIFICATION_UPDATED', refresh)
    socket.on('NOTIFICATION_DELETED', refresh)

    return () => {
      socket.off(LIVE_CHAT_SOCKET_EVENTS.MESSAGE_CREATED, refresh)
      socket.off('NOTIFICATION_CREATED', refresh)
      socket.off('NOTIFICATION_UPDATED', refresh)
      socket.off('NOTIFICATION_DELETED', refresh)
    }
  }, [enabled, queryClient, socket])

  return query
}
